import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserShield,
  faBell,
  faUser,
  faShieldAlt,
  faKey,
  faEnvelope,
  faMobileAlt,
  faChevronRight,
  faExchangeAlt,
} from "@fortawesome/free-solid-svg-icons";
import ChangePasswordModal from "../../usermgmt/ChangePasswordModal";
import MfaSetupModal from "../../usermgmt/MfaSetupModal";
import MfaAPI from "../../services/MfaAPI";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "./css/SecuritySettings.css";

const SecuritySettings = ({
  onMfaStatusChange,
  isDemoSeat = false,
  initialAction,
}) => {
  const { t } = useTranslation();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showMfaSetupModal, setShowMfaSetupModal] = useState(false);
  const [selectedMfaMethod, setSelectedMfaMethod] = useState("sms");
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showMethodsDetail, setShowMethodsDetail] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [mfaStatus, setMfaStatus] = useState({
    enabled: false,
    method: null,
    maskedPhone: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Auto-open modal based on initialAction prop
  useEffect(() => {
    if (initialAction === "changePassword") {
      setShowChangePasswordModal(true);
    } else if (initialAction === "mfa") {
      setShowMfaSetupModal(true);
    } else if (initialAction === "disableMfa") {
      setShowDisableConfirm(true);
    }
  }, [initialAction]);

  useEffect(() => {
    loadMfaStatus();
  }, []);

  const loadMfaStatus = async () => {
    try {
      const response = await MfaAPI.getMfaStatus();
      console.log("SecuritySettings: MFA status response:", response);
      if (response) {
        const status = {
          enabled: response.enabled || false,
          method: response.method || null,
          maskedPhone: response.maskedPhone || null,
        };
        setMfaStatus(status);
        // Notify parent (Settings.js) for the posture banner
        if (onMfaStatusChange) onMfaStatusChange(status);
      }
    } catch (err) {
      console.error("Failed to load MFA status:", err);
      const status = {
        enabled: false,
        method: null,
        maskedPhone: null,
      };
      setMfaStatus(status);
      if (onMfaStatusChange) onMfaStatusChange(status);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePasswordClick = () => {
    setShowChangePasswordModal(true);
  };

  const handleCloseModal = () => {
    setShowChangePasswordModal(false);
  };

  const handleEnableMfa = (method = "sms") => {
    setSelectedMfaMethod(method);
    setShowMfaSetupModal(true);
    setError("");
  };

  const handleMfaSetupComplete = () => {
    setShowMfaSetupModal(false);
    toast.success(t("security.mfa.enabledSuccess"));
    loadMfaStatus();
  };

  const handleDisableMfa = async () => {
    if (!disablePassword.trim()) {
      setError(t("security.mfa.enterPassword"));
      return;
    }

    try {
      await MfaAPI.disableMfa(disablePassword);
      setShowDisableConfirm(false);
      setDisablePassword("");
      setError("");
      toast.success(t("security.mfa.disabledSuccess"));
      loadMfaStatus();
    } catch (err) {
      setError(err.error || err.message || t("security.mfa.disableFailed"));
    }
  };

  const handleRegenerateBackupCodes = async () => {
    const password = prompt(t("security.mfa.regenPasswordPrompt"));
    if (!password) return;

    try {
      const response = await MfaAPI.regenerateBackupCodes(password);

      // Download the codes
      const codesText = response.backupCodes.join("\n");
      const blob = new Blob([codesText], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "mfa-backup-codes-new.txt";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(t("security.mfa.regenSuccess"));
    } catch (err) {
      setError(err.error || t("security.mfa.regenFailed"));
      setTimeout(() => setError(""), 5000);
    }
  };

  return (
    <>
      <div className="common-form-group">
        <h4>{t("security.mfa.title")}</h4>

        {isDemoSeat ? (
          <div
            className="common-message info"
            title={t("common.notAvailableDemo")}
            style={{
              opacity: 0.6,
              cursor: "not-allowed",
              pointerEvents: "none",
            }}
          >
            <span
              style={{
                fontStyle: "italic",
                color: "var(--text-secondary, #888)",
              }}
            >
              {t("security.mfa.demoUnavailable")}
            </span>
          </div>
        ) : (
          <>
            {!isLoading && mfaStatus && (
              <>
                {mfaStatus.enabled ? (
                  <>
                    <div className="mfa-enabled-box">
                      <div className="mfa-enabled-header">
                        <FontAwesomeIcon
                          icon={faShieldAlt}
                          className="mfa-enabled-icon"
                        />
                        <div>
                          <strong className="mfa-enabled-title">
                            {t("security.mfa.enabled")}
                          </strong>
                          <div className="mfa-enabled-details">
                            {t("security.mfa.methodLabel")}{" "}
                            {mfaStatus.method === "email"
                              ? t("security.mfa.methodEmail")
                              : t("security.mfa.methodSms")}{" "}
                            •{" "}
                            {mfaStatus.method === "email"
                              ? t("security.mfa.methodEmail")
                              : t("security.mfa.phoneLabel")}
                            : {mfaStatus.maskedPhone}
                          </div>
                        </div>
                      </div>
                      <div className="mfa-enabled-buttons">
                        <button
                          className="common-button secondary mfa-action-button"
                          onClick={handleRegenerateBackupCodes}
                        >
                          <FontAwesomeIcon icon={faKey} />{" "}
                          {t("security.mfa.regenBackupCodes")}
                        </button>
                        <button
                          className="common-button danger mfa-action-button"
                          onClick={() => setShowDisableConfirm(true)}
                        >
                          {t("security.mfa.disable")}
                        </button>
                      </div>
                    </div>

                    {showDisableConfirm && (
                      <div className="mfa-disable-confirm">
                        <h5 className="mfa-disable-title">
                          {t("security.mfa.disableConfirmTitle")}
                        </h5>
                        <p className="mfa-disable-text">
                          {t("security.mfa.disableConfirmText")}
                        </p>
                        <input
                          type="password"
                          placeholder={t("security.mfa.passwordPlaceholder")}
                          value={disablePassword}
                          onChange={(e) => {
                            setDisablePassword(e.target.value);
                            setError("");
                          }}
                          className="common-input mfa-disable-input"
                        />
                        {error && <div className="error-message">{error}</div>}
                        <div className="mfa-disable-buttons">
                          <button
                            className="common-button secondary mfa-action-button"
                            onClick={() => {
                              setShowDisableConfirm(false);
                              setDisablePassword("");
                              setError("");
                            }}
                          >
                            {t("common.cancel")}
                          </button>
                          <button
                            className="common-button danger mfa-action-button"
                            onClick={handleDisableMfa}
                          >
                            {t("security.mfa.disable")}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Available MFA Methods — collapsible sub-section */}
                    <button
                      className="settings-sub-reveal"
                      onClick={() => setShowMethodsDetail(!showMethodsDetail)}
                    >
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className={`sub-reveal-chevron ${
                          showMethodsDetail ? "expanded" : ""
                        }`}
                      />
                      <FontAwesomeIcon icon={faExchangeAlt} />
                      {t("security.mfa.viewChangeMethods")}
                    </button>

                    {showMethodsDetail && (
                      <div className="mfa-methods-container">
                        <p className="mfa-methods-title">
                          {t("security.mfa.availableMethods")}
                        </p>

                        <div className="mfa-methods-grid">
                          {/* SMS Text Message */}
                          <div
                            className={`mfa-method-card ${
                              mfaStatus.method === "sms" ? "sms-active" : ""
                            }`}
                          >
                            <FontAwesomeIcon
                              icon={faBell}
                              className="mfa-method-icon sms"
                            />
                            <div>
                              <strong className="mfa-method-title">
                                {t("security.mfa.smsMethod")}
                              </strong>
                              <span className="mfa-method-description">
                                {t("security.mfa.smsDescription")}
                              </span>
                            </div>
                            {mfaStatus.method === "sms" && (
                              <span className="mfa-active-badge">
                                {t("security.mfa.activeBadge")}
                              </span>
                            )}
                          </div>

                          {/* Email */}
                          <div
                            className={`mfa-method-card ${
                              mfaStatus.method === "email" ? "email-active" : ""
                            }`}
                          >
                            <FontAwesomeIcon
                              icon={faEnvelope}
                              className="mfa-method-icon email"
                            />
                            <div>
                              <strong className="mfa-method-title">
                                {t("security.mfa.emailMethod")}
                              </strong>
                              <span className="mfa-method-description">
                                {t("security.mfa.emailDescription")}
                              </span>
                            </div>
                            {mfaStatus.method === "email" && (
                              <span className="mfa-active-badge">
                                {t("security.mfa.activeBadge")}
                              </span>
                            )}
                          </div>

                          {/* Authentication App */}
                          <div
                            className="mfa-method-card disabled"
                            title={t("common.comingSoon")}
                          >
                            <FontAwesomeIcon
                              icon={faMobileAlt}
                              className="mfa-method-icon app"
                            />
                            <div>
                              <strong className="mfa-method-title">
                                {t("security.mfa.appMethod")}
                              </strong>
                              <span className="mfa-method-description">
                                {t("security.mfa.appDescription")}
                              </span>
                            </div>
                            <span className="mfa-coming-soon-badge">
                              {t("common.comingSoon")}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mfa-setup-container">
                    <p className="mfa-setup-text">
                      {t("security.mfa.setupDescription")}
                    </p>

                    <div className="mfa-setup-grid">
                      {/* SMS Text Message */}
                      <div
                        className="mfa-method-card clickable sms-hover"
                        onClick={() => handleEnableMfa("sms")}
                      >
                        <FontAwesomeIcon
                          icon={faBell}
                          className="mfa-method-icon sms"
                        />
                        <div>
                          <strong className="mfa-method-title">
                            {t("security.mfa.smsMethod")}
                          </strong>
                          <span className="mfa-method-description">
                            {t("security.mfa.smsDescription")}
                          </span>
                        </div>
                      </div>

                      {/* Email */}
                      <div
                        className="mfa-method-card clickable email-hover"
                        onClick={() => handleEnableMfa("email")}
                      >
                        <FontAwesomeIcon
                          icon={faEnvelope}
                          className="mfa-method-icon email"
                        />
                        <div>
                          <strong className="mfa-method-title">
                            {t("security.mfa.emailMethod")}
                          </strong>
                          <span className="mfa-method-description">
                            {t("security.mfa.emailDescription")}
                          </span>
                        </div>
                      </div>

                      {/* Authentication App */}
                      <div
                        className="mfa-method-card clickable disabled"
                        title={t("common.comingSoon")}
                      >
                        <FontAwesomeIcon
                          icon={faMobileAlt}
                          className="mfa-method-icon app"
                        />
                        <div>
                          <strong className="mfa-method-title">
                            {t("security.mfa.appMethod")}
                          </strong>
                          <span className="mfa-method-description">
                            {t("security.mfa.appDescription")}
                          </span>
                        </div>
                        <span className="mfa-coming-soon-badge">
                          {t("common.comingSoon")}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      <div className="common-form-group">
        <h4>{t("security.password.title")}</h4>
        <div className="common-button-group password-button-group">
          <button
            className="common-button secondary"
            onClick={isDemoSeat ? undefined : handleChangePasswordClick}
            disabled={isDemoSeat}
            title={isDemoSeat ? t("common.notAvailableDemo") : undefined}
            style={
              isDemoSeat ? { opacity: 0.5, cursor: "not-allowed" } : undefined
            }
          >
            {t("security.password.change")}
          </button>
          {isDemoSeat && (
            <span
              style={{
                fontSize: "12px",
                color: "var(--text-secondary, #888)",
                fontStyle: "italic",
                marginLeft: 8,
              }}
            >
              {t("common.notAvailableDemo")}
            </span>
          )}
        </div>
      </div>

      {showChangePasswordModal && (
        <ChangePasswordModal
          onClose={handleCloseModal}
          setMessage={setMessage}
        />
      )}

      {showMfaSetupModal && (
        <MfaSetupModal
          method={selectedMfaMethod}
          onClose={() => setShowMfaSetupModal(false)}
          onComplete={handleMfaSetupComplete}
        />
      )}

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}
    </>
  );
};

export default SecuritySettings;
